import {
  Box,
  Button,
  Link,
  Skeleton,
  Typography,
} from '@customink/pigment-react';
import { useZipCode } from '@customink/project-service-react-components';
import Collapse from '@mui/material/Collapse';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEventHandler } from 'react';

import { ZipCodeTextField } from '@/components/LineItem';
import { rollbar } from '@/config/rollbar.config';
import { useInternalPreview } from '@/hooks/internalPreview';
import { useUpdateZipCodeSync } from '@/hooks/Project';
import { useValidateZipCode } from '@/hooks/useValidateZipCode';
import {
  PgmntColorInteractiveTextWeak,
  PgmntColorNeutralTextSecondary,
  spacing8,
} from '@/utils/designTokens';
import { isNotFoundError } from '@/utils/requests';

export function EditZipCode() {
  const {
    zipCode,
    isError: isZipCodeRequestError,
    isLoading: isLoadingProject,
    isUninitialized: zipCodeIsUninitialized,
  } = useZipCode();

  const [
    validateZipCode,
    {
      isError: isValidationRequestError,
      error: validationRequestError,
      isLoading: isValidating,
      isUninitialized: isPreValidation,
      data: validationResponse,
    },
  ] = useValidateZipCode();

  const [
    updateZipCode,
    { isLoading: isUpdatingZipCode, isError: isUpdateProjectRequestError },
  ] = useUpdateZipCodeSync();

  const [zipCodeValue, setZipCodeValue] = useState<string>(zipCode || '');
  const [showTextField, setShowTextField] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<boolean>(false);
  const [initialPageLoad, setInitialPageLoad] = useState<boolean>(true);
  const [city, setCity] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const zipCodeTextFieldRef = useRef<HTMLInputElement>(null);
  const isInternalPreview = useInternalPreview();

  useEffect(() => {
    if (
      isZipCodeRequestError ||
      isUpdateProjectRequestError ||
      // the 404 case is handled elsewhere
      (isValidationRequestError && !isNotFoundError(validationRequestError))
    ) {
      // TODO: Handle network errors
      rollbar.error('Unhandled network error.', validationRequestError);
    }
  }, [
    isZipCodeRequestError,
    isUpdateProjectRequestError,
    isValidationRequestError,
    validationRequestError,
  ]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setZipCodeValue(event.target.value);
  };

  const handleToggleTextField = () => {
    setShowTextField((current) => !current);
  };

  const handleCancel = () => {
    setZipCodeValue(zipCode || ''); // Reset value on cancel.
    setShowTextField(false);
  };

  const handleUpdate = useCallback(async () => {
    // The update button should be disabled while we load project data
    if (isLoadingProject) {
      rollbar.error('Cannot update while project is loading.');
      return;
    }

    const response = await validateZipCode({
      zipCode: zipCodeValue,
    });

    if ('error' in response) {
      if (isNotFoundError(response.error)) {
        rollbar.info(response.error);
      } else {
        rollbar.error(response.error);
      }
      return;
    }
    updateZipCode(zipCodeValue);
    setShowTextField(false);
  }, [isLoadingProject, updateZipCode, validateZipCode, zipCodeValue]);

  // enter to submit
  useEffect(() => {
    if (!showTextField) return; // Only add listener when text field is visible

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        handleUpdate();
      }
    };

    zipCodeTextFieldRef.current?.addEventListener('keydown', handleKeyDown);
    return () =>
      zipCodeTextFieldRef.current?.removeEventListener(
        'keydown',
        handleKeyDown,
      );
  }, [handleUpdate, showTextField]);

  // Reset on unmount.
  useEffect(
    () => () => {
      setInitialPageLoad(true);
    },
    [],
  );

  const isLoading =
    isLoadingProject ||
    zipCodeIsUninitialized ||
    isValidating ||
    (zipCode && isPreValidation) ||
    (!zipCode && !isPreValidation);

  useEffect(() => {
    if (!isLoading) {
      setInitialPageLoad(false);
    }
  }, [isLoading]);

  // Make updates when loading is complete.
  // Update the zipCodeValue whenever we load a new value for the zipCode.
  useEffect(() => {
    if (!zipCodeIsUninitialized && !isLoadingProject && zipCode) {
      setZipCodeValue(zipCode);
    }
  }, [isLoadingProject, zipCodeIsUninitialized, zipCode]);

  // Get the city and state whenever the zipCode changes.
  // This request also validates the zip code.
  useEffect(() => {
    if (zipCode) {
      validateZipCode({ zipCode });
    }
  }, [validateZipCode, zipCode]);

  // Run this every time we call the validate endpoint.
  // That should happen once on page load (if a zip code is present)
  // and once every time the user clicks "Update".
  // We only hide the text field after the user clicks "Update".
  useEffect(() => {
    if (
      isUpdatingZipCode ||
      isValidating ||
      !(validationResponse || isNotFoundError(validationRequestError))
    )
      return;

    const showZipCodeAlert = (show: boolean) => {
      setValidationError(show);
      if (show) {
        setShowTextField(true); // No hide, only show.
      }
    };

    showZipCodeAlert(
      !validationResponse || isNotFoundError(validationRequestError),
    );

    if (validationResponse?.city && validationResponse?.state) {
      setCity(validationResponse.city);
      setState(validationResponse.stateCode);
    }
  }, [
    isValidating,
    isUpdatingZipCode,
    validationResponse,
    validationRequestError,
  ]);

  const zipCodeIsValid = !!zipCode && !validationError;
  const disableUpdateActions =
    isValidating || isUpdatingZipCode || isLoadingProject || !zipCodeValue;
  const disableCancel = isValidating || isUpdatingZipCode || isLoadingProject;
  const showLoadingSkeleton = isLoading && initialPageLoad;
  const showEditZipCode =
    showTextField ||
    !zipCodeIsValid ||
    (city !== null && !city) ||
    (state !== null && !state);

  return (
    <Box>
      {showLoadingSkeleton && (
        <Typography pt="0.125rem" variant="uiTextTiny">
          <Skeleton data-testid="zip-code-skeleton" width={45} />
        </Typography>
      )}

      {!showLoadingSkeleton && showEditZipCode && (
        <Typography pt="0.125rem" variant="uiTextSmall">
          Change postal code{' '}
          {!zipCode || !city || !state
            ? null
            : !isInternalPreview && (
                <Link
                  data-testid="delivery-line-item-cancel-link"
                  disabled={disableCancel}
                  onClick={handleCancel}
                >
                  cancel
                </Link>
              )}
        </Typography>
      )}

      {!showLoadingSkeleton && !showEditZipCode && (
        <Typography
          color={PgmntColorNeutralTextSecondary}
          component="div"
          variant="uiTextTiny"
        >
          {`${city}, ${state}`}{' '}
          {!isInternalPreview && (
            <Link
              color={PgmntColorInteractiveTextWeak}
              data-testid="delivery-line-item-change-link"
              onClick={handleToggleTextField}
              sx={{ textDecoration: 'underline' }}
            >
              change
            </Link>
          )}
        </Typography>
      )}

      <Collapse in={showEditZipCode} ref={zipCodeTextFieldRef}>
        <Box display="flex" gap={spacing8} pt="0.25rem">
          <ZipCodeTextField
            isDisabled={isValidating || isUpdatingZipCode}
            isError={!zipCodeIsValid}
            onChange={handleChange}
            zipCode={zipCodeValue}
          />
          <Button
            data-testid="delivery-line-item-update-button"
            disabled={disableUpdateActions}
            onClick={handleUpdate}
            sx={{
              minWidth: 80,
            }}
          >
            {(isValidating || isUpdatingZipCode) && !initialPageLoad
              ? 'Updating...'
              : 'Update'}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}
